import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, uploadImage, deleteImage } from '../supabaseClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user) {
        loadUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes (including email confirmation)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (session?.user) {
        // Check if user email is confirmed
        if (session.user.email_confirmed_at) {
          loadUserProfile(session.user.id);
        } else {
          setUser(null);
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Load user profile from profiles table
  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      
      setUser(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Profile will be loaded by the auth state change listener
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      const { email, password, name, phone, role } = userData;

      // Sign up with Supabase Auth - pass metadata for trigger
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name,
            phone: phone || null,
            role: role || 'renter',
          }
        }
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Registration failed');
      }

      // Try to create profile manually (fallback if trigger fails)
      // Wait a moment for trigger to potentially complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check if profile was created by trigger
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', authData.user.id)
        .single();

      // If trigger didn't create profile, create it manually
      if (!existingProfile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert([
            {
              id: authData.user.id,
              email,
              name,
              phone: phone || null,
              role: role || 'renter',
            },
          ]);

        if (profileError) {
          console.error('Profile creation error:', profileError);
          throw new Error('Failed to create user profile: ' + profileError.message);
        }
      }

      return { success: true, user: authData.user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setSession(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Update profile function
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Reload profile
      await loadUserProfile(user.id);
      
      return { success: true };
    } catch (error) {
      console.error('Update profile error:', error);
      return { success: false, error: error.message };
    }
  };

  // Change password function
  const changePassword = async (currentPassword, newPassword) => {
    try {
      if (!user) throw new Error('No user logged in');

      // Supabase doesn't verify current password, so we'll try to sign in with it first
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (verifyError) {
        throw new Error('Current password is incorrect');
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Change password error:', error);
      return { success: false, error: error.message };
    }
  };

  // Upload profile picture function
  const uploadProfilePicture = async (file) => {
    try {
      if (!user) throw new Error('No user logged in');

      // Delete old profile picture if exists
      if (user.profile_picture) {
        const oldPath = user.profile_picture.split('/').pop();
        await deleteImage('profile-pictures', `${user.id}/${oldPath}`);
      }

      // Upload new profile picture
      const result = await uploadImage(file, 'profile-pictures', user.id);
      
      if (!result.success) throw new Error(result.error);

      // Update profile with new picture URL
      const { error } = await supabase
        .from('profiles')
        .update({ profile_picture: result.url })
        .eq('id', user.id);

      if (error) throw error;

      // Reload profile
      await loadUserProfile(user.id);

      return { success: true, url: result.url };
    } catch (error) {
      console.error('Upload profile picture error:', error);
      return { success: false, error: error.message };
    }
  };

  // Remove profile picture function
  const removeProfilePicture = async () => {
    try {
      if (!user) throw new Error('No user logged in');
      if (!user.profile_picture) throw new Error('No profile picture to remove');

      // Delete from storage
      const path = user.profile_picture.split('/').pop();
      await deleteImage('profile-pictures', `${user.id}/${path}`);

      // Update profile
      const { error } = await supabase
        .from('profiles')
        .update({ profile_picture: null })
        .eq('id', user.id);

      if (error) throw error;

      // Reload profile
      await loadUserProfile(user.id);

      return { success: true };
    } catch (error) {
      console.error('Remove profile picture error:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete account function
  const deleteAccount = async (password) => {
    try {
      if (!user) throw new Error('No user logged in');

      // Verify password first
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: password,
      });

      if (verifyError) {
        throw new Error('Password is incorrect');
      }

      // Delete profile picture if exists
      if (user.profile_picture) {
        const path = user.profile_picture.split('/').pop();
        await deleteImage('profile-pictures', `${user.id}/${path}`);
      }

      // Note: Deleting from profiles will cascade delete related data due to foreign keys
      // The auth.users deletion should be handled via RPC or admin API
      // For now, we'll just delete the profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (profileError) throw profileError;

      // Sign out
      await supabase.auth.signOut();
      
      setUser(null);
      setSession(null);

      return { success: true };
    } catch (error) {
      console.error('Delete account error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    session,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    uploadProfilePicture,
    removeProfilePicture,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
